import { useEffect, useRef } from "react";
import type { UseFormSetValue } from "react-hook-form";
import { fullDaySlot } from "../../../shared/lib/schedule";
import type { TechnicianShift } from "../../../entities/technician-profile";
import type { AppointmentFormValues } from "./appointmentForm.schema";

/**
 * Ganztägig, both directions.
 *
 * Ticking the box reshapes the Termin to fill the technician's shift; reshaping the Termin by hand
 * unticks the box. Nothing is disabled — the durations stay ordinary inputs, and the board block
 * stays draggable — so the flag has to follow the values rather than lock them.
 *
 * Everything hinges on telling the hook's **own** writes apart from everybody else's. It keeps a
 * baseline of exactly what it last wrote; anything that does not match it came from the user, either
 * through the form or through a drag on the board (which lands here as the same `setValue`s, via
 * `defaultSlot`). That is why there is no separate board branch — one comparison covers both.
 *
 * The four rules, in the order the effect applies them:
 *
 * 1. **First look after a seed → adopt, never derive.** A stored all-day appointment opens with the
 *    durations it was saved with. Re-deriving on open would silently rewrite a saved Termin because
 *    somebody edited the shift in the meantime, and would mark a form dirty that the user has not
 *    touched.
 * 2. **A hand edit unticks.** Durations, or the time-of-day of `plannedStart`, differing from the
 *    baseline. Checked *before* rule 3 so that a drag which moves and reassigns in one gesture is
 *    read as a move, not as a technician change.
 * 3. **A different technician or a different day re-derives.** Changing the date keeps it all-day and
 *    re-snaps onto the new day — it does not untick, which is what rule 2's time-only comparison
 *    buys. Neither of those alters the shape, so rule 2 has already passed by the time this runs.
 * 4. **Deriving waits for the shift.** `useTechnicianProfiles` may still be in flight when the box is
 *    ticked, and `plannedStart` may not carry a day yet on a create opened from the work-order page.
 *    The intent is remembered and spent as soon as both arrive — the arrival of the profile alone
 *    never derives anything, or rule 1 would be undone the moment the query resolved.
 *
 * PLANNED SIDE ONLY, deliberately: Ganztägig describes a *plan* to fill a shift, and a completed
 * appointment is no longer one. Once the status is done the board drags the Ist-Zeiten instead
 * (`appointmentForm.schema.ts`, `toSlot`), so rule 2 stops seeing changes and the box keeps whatever
 * it was saved with. That is the intended outcome, not an oversight — an all-day flag is not a claim
 * about how long the work actually took.
 */

interface Shape {
  /** `HH:mm` of `plannedStart`. The date is deliberately excluded — see rule 3. */
  time: string;
  travel: number;
  work: number;
  departure: number;
}

interface Params {
  /** The modal is open and seeded. Nothing is derived from a form that is about to be replaced. */
  enabled: boolean;
  isFullDay: boolean;
  technicianId: number | null;
  plannedStart: string;
  travel: number;
  work: number;
  departure: number;
  shift: TechnicianShift | null;
  setValue: UseFormSetValue<AppointmentFormValues>;
}

export function useFullDaySync({
  enabled,
  isFullDay,
  technicianId,
  plannedStart,
  travel,
  work,
  departure,
  shift,
  setValue,
}: Params): void {
  const initialized = useRef(false);
  const wasFullDay = useRef(false);
  const baseline = useRef<Shape | null>(null);
  const lastKey = useRef<string | null>(null);
  /** A derivation asked for but not yet possible — no shift loaded, or no day typed. */
  const pending = useRef(false);

  useEffect(() => {
    if (!enabled) {
      initialized.current = false;
      wasFullDay.current = false;
      baseline.current = null;
      lastKey.current = null;
      pending.current = false;
      return;
    }

    const dayIso = datePart(plannedStart);
    const current: Shape = { time: timePart(plannedStart), travel, work, departure };
    // Only what the *user* controls. The shift is not in here on purpose: a profile arriving from
    // its query must not count as a change, or rule 1 would be undone a moment after it applied.
    const key = `${technicianId ?? ""}|${dayIso ?? ""}`;

    // Rule 1.
    if (!initialized.current) {
      initialized.current = true;
      wasFullDay.current = isFullDay;
      baseline.current = isFullDay ? current : null;
      lastKey.current = key;
      return;
    }

    if (!isFullDay) {
      wasFullDay.current = false;
      baseline.current = null;
      pending.current = false;
      lastKey.current = key;
      return;
    }

    if (!wasFullDay.current) {
      wasFullDay.current = true;
      pending.current = true;
    } else if (baseline.current && changed(current, baseline.current)) {
      // Rule 2. `shouldDirty` because it is a real change to what will be submitted, even though
      // the user's gesture was aimed at a duration rather than at the checkbox.
      setValue("isFullDay", false, { shouldDirty: true });
      return;
    } else if (key !== lastKey.current) {
      // Rule 3.
      pending.current = true;
    }

    lastKey.current = key;

    // Rule 4.
    if (!pending.current) return;
    if (!shift || !dayIso) return;

    const next = fullDaySlot({
      dayIso,
      shiftStart: shift.shiftStart,
      shiftEnd: shift.shiftEnd,
      travelMinutes: travel,
      departureMinutes: departure,
    });

    pending.current = false;

    // An unusable shift: leave the form exactly as the user left it and adopt it as the baseline, so
    // the untick rule does not then fire on values this hook never wrote.
    if (!next) {
      baseline.current = current;
      return;
    }

    setValue("plannedStart", next.plannedStart, { shouldDirty: true });
    setValue("plannedWorkDuration", next.plannedWorkDuration, { shouldDirty: true });

    // The baseline is what was just *written*, not what is on screen — these two `setValue`s have
    // not re-rendered yet. Recording `current` here would make the next pass see a mismatch and
    // untick the box this very derivation was asked for.
    baseline.current = {
      time: timePart(next.plannedStart),
      travel,
      work: next.plannedWorkDuration,
      departure,
    };
  }, [
    enabled,
    isFullDay,
    technicianId,
    plannedStart,
    travel,
    work,
    departure,
    shift,
    setValue,
  ]);
}

function changed(a: Shape, b: Shape): boolean {
  return (
    a.time !== b.time ||
    a.travel !== b.travel ||
    a.work !== b.work ||
    a.departure !== b.departure
  );
}

/** `YYYY-MM-DD` of a `datetime-local` value, or `null` while it is incomplete. */
function datePart(value: string): string | null {
  const match = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}/.exec(value);
  return match ? match[1] : null;
}

/** `HH:mm` of a `datetime-local` value, or `""` while it is incomplete. */
function timePart(value: string): string {
  const match = /^\d{4}-\d{2}-\d{2}T(\d{2}:\d{2})/.exec(value);
  return match ? match[1] : "";
}
